import { urls } from '~/preloaded';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { get, patch } from '~/helpers/api';

import type { InferActionsType } from '~/Reducers';
import type { AppAsyncThunk } from '~/store';
import type {
  IApiGetClanTasksResponse,
  IApiPatchClaimClanTaskResponse,
  IApiPatchClaimClanTasksResponse,
} from '~/types/api';

export const CLAIM_CLAN_TASK = 'CLAIM_CLAN_TASK';
export const CLAIM_CLAN_TASK_RESPONSE = 'CLAIM_CLAN_TASK_RESPONSE';
export const GET_CLAN_TASKS = 'GET_CLAN_TASKS';
export const GET_CLAN_TASKS_RESPONSE = 'GET_CLAN_TASKS_RESPONSE';
export const CLAIM_CLAN_TASKS = 'CLAIM_CLAN_TASKS';
export const CLAIM_CLAN_TASKS_RESPONSE = 'CLAIM_CLAN_TASKS_RESPONSE';

export interface ClanTaskReward {
  oil?: number;
  coal?: number;
}

export interface ClanTask {
  taskId: number;
  clanId: number;
  name: string;
  order: number;
  claimedAt?: string;
  finishedTill?: string;
  progress: number;
  isFinished: boolean;
  isClaimed: boolean;
  reward?: ClanTaskReward;
}

export type IClaimClanTasks = {
  unchanged: ClanTask[];
  changed: ClanTask[];
  reward?: ClanTaskReward;
};

export type ActionsType = InferActionsType<typeof actionsClanTask>;

const actionsClanTask = {
  claimClanTask: () =>
    ({
      type: CLAIM_CLAN_TASK,
    }) as const,

  getClanTasks: () =>
    ({
      type: GET_CLAN_TASKS,
    }) as const,

  claimClanTasks: () =>
    ({
      type: CLAIM_CLAN_TASKS,
    }) as const,

  getClanTaskResponse: (tasks: ClanTask[]) =>
    ({
      type: GET_CLAN_TASKS_RESPONSE,
      payload: tasks,
    }) as const,

  claimTaskResponse: (data: ClanTask) =>
    ({
      type: CLAIM_CLAN_TASK_RESPONSE,
      payload: data,
    }) as const,

  claimTasksResponse: (data: IClaimClanTasks) =>
    ({
      type: CLAIM_CLAN_TASKS_RESPONSE,
      payload: data,
    }) as const,
};

export const getClanTasksThunk = (): AppAsyncThunk => async (dispatch) => {
  dispatch(actionsClanTask.getClanTasks());

  try {
    const response = await get<IApiGetClanTasksResponse>(urls.darwinGetTasks);
    if (response?.error || response?.additional_info) {
      throw response;
    }

    dispatch(actionsClanTask.getClanTaskResponse(response.items));
  } catch (error) {
    console.error('Failed sync account info', error);
  }
};

export const claimClanTaskThunk =
  (taskId: number): AppAsyncThunk =>
  async (dispatch) => {
    dispatch(actionsClanTask.claimClanTask());
    dwhExport.push(DWH_EVENTS.TASKS.CLAIM_TASK, { task_id: taskId });

    const url = urls.darwinClaimTask.replace('{task_id}', String(taskId));

    try {
      const response = await patch<IApiPatchClaimClanTaskResponse>(url);
      if (response?.error || response?.additional_info) {
        throw response;
      }

      dispatch(actionsClanTask.claimTaskResponse(response));
    } catch (error) {
      console.error('Failed sync account info', error);

      void dispatch(getClanTasksThunk());
    }
  };

export const claimClanTasksThunk = (): AppAsyncThunk => async (dispatch) => {
  dispatch(actionsClanTask.claimClanTasks());
  dwhExport.push(DWH_EVENTS.TASKS.CLAIM_ALL);

  try {
    const response = await patch<IApiPatchClaimClanTasksResponse>(urls.darwinClaimAllTasks);
    if (response?.error || response?.additional_info) {
      throw response;
    }

    dispatch(actionsClanTask.claimTasksResponse(response));
  } catch (error) {
    console.error('Failed sync account info', error);

    void dispatch(getClanTasksThunk());
  }
};
